/**
 * V2MOM model — pure helpers shared by the store, the pages, the print body
 * and the tests. No I/O here.
 *
 * A V2MOM is one page: a Vision, a short list of Values, a numbered list of
 * Methods, and for each Method the Obstacles in its way and the Measures it is
 * held to. Obstacles and measures may also stand on their own when they are
 * not about one method.
 *
 * The process around the page (migration 945): a V2MOM is asked for, written
 * against the one above it, sent to the owner of that one for review, approved
 * (or returned with a note), locked for the period, and reopened mid-period
 * only with that reviewer's say-so. The reviewer is always the owner of the
 * parent V2MOM — the parent link is the cascade (canon 01 "V2MOM").
 */

export type V2momStatus = 'draft' | 'in_review' | 'published' | 'locked';
export type V2momEntryKind = 'value' | 'method' | 'obstacle' | 'measure';
export type V2momAskStatus = 'open' | 'fulfilled' | 'withdrawn';

export interface V2mom {
  id: string;
  workspaceId: string;
  title: string;
  vision: string | null;
  ownerId: string | null;
  orgUnitId: string | null;
  parentId: string | null;
  periodLabel: string | null;
  status: V2momStatus;
  publishedAt: string | null;
  lockedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** The day it is due to be sent for review (945). */
  dueOn: string | null;
  submittedAt: string | null;
  submittedBy: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  /** What the reviewer said when they last approved or returned it. */
  reviewNote: string | null;
  publishedBy: string | null;
  lockedBy: string | null;
  /** A pending request to reopen a published or locked V2MOM. */
  reopenRequestedAt: string | null;
  reopenRequestedBy: string | null;
  reopenNote: string | null;
}

export interface V2momEntry {
  id: string;
  v2momId: string;
  workspaceId: string;
  kind: V2momEntryKind;
  methodId: string | null;
  position: number;
  title: string;
  detail: string | null;
  target: string | null;
  objectiveId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** An ask to write a V2MOM against a published one, by a date (945). */
export interface V2momAsk {
  id: string;
  workspaceId: string;
  parentV2momId: string;
  askedUserId: string;
  askedBy: string | null;
  dueOn: string | null;
  note: string | null;
  status: V2momAskStatus;
  /** The draft the person started from this ask, once they have. */
  v2momId: string | null;
  fulfilledAt: string | null;
  withdrawnAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One line of a V2MOM's record: what happened, who did it, what they said (945). */
export interface V2momEvent {
  id: string;
  v2momId: string;
  workspaceId: string;
  event: string;
  actorId: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  note: string | null;
  createdAt: string;
}

export interface V2momMethodGroup {
  method: V2momEntry;
  obstacles: V2momEntry[];
  measures: V2momEntry[];
}

export interface V2momTree {
  values: V2momEntry[];
  methods: V2momMethodGroup[];
  /** Obstacles not tied to a method. */
  obstacles: V2momEntry[];
  /** Measures not tied to a method. */
  measures: V2momEntry[];
}

export const V2MOM_SECTIONS = [
  { key: 'vision', label: 'Vision', question: 'What do we want?' },
  { key: 'values', label: 'Values', question: 'What is important to us as we pursue it?' },
  { key: 'methods', label: 'Methods', question: 'How do we get it done?' },
  { key: 'obstacles', label: 'Obstacles', question: 'What is in the way?' },
  { key: 'measures', label: 'Measures', question: 'How do we know we got there?' },
] as const;

export type V2momSectionKey = (typeof V2MOM_SECTIONS)[number]['key'];

export const V2MOM_STATUS_LABEL: Record<V2momStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  published: 'Published',
  locked: 'Locked',
};

/** What each line of the record says, in plain words. */
export const V2MOM_EVENT_LABEL: Record<string, string> = {
  created: 'Created',
  realigned: 'Realigned',
  owner_changed: 'Owner changed',
  submitted: 'Sent for review',
  withdrawn: 'Taken back from review',
  approved: 'Accepted',
  returned: 'Returned',
  published: 'Published',
  locked: 'Locked',
  reopen_requested: 'Asked to reopen',
  reopened: 'Reopened',
  reopen_declined: 'Reopening declined',
  asked: 'Asked others to write theirs',
  ask_withdrawn: 'An ask withdrawn',
};

/** The database's refusals, in the words the screen shows. */
export const V2MOM_RPC_ERROR_MESSAGE: Record<string, string> = {
  not_signed_in: 'You are not signed in.',
  not_found: 'This V2MOM no longer exists.',
  not_a_member: 'You are not a member of this workspace.',
  not_allowed: 'Only the owner of this V2MOM, the person who created it, or a workspace admin can do that.',
  not_the_reviewer: 'Only the owner of the V2MOM this one aligns to (or a workspace admin) can do that.',
  not_a_draft: 'This V2MOM is not a draft, so its content cannot change right now.',
  not_in_review: 'This V2MOM is not in review.',
  not_published: 'This V2MOM is not published.',
  note_required: 'Say why — a note is required.',
  no_request: 'There is no pending request to reopen it.',
  parent_cycle: 'A V2MOM cannot align to itself or to one written against it.',
  parent_not_found: 'That parent V2MOM is not in this workspace.',
  parent_not_published: 'Publish this V2MOM before asking people to write theirs against it.',
  owner_must_be_self: 'Only a workspace admin can create a V2MOM for someone else.',
  owner_not_a_member: 'The owner must be a member of this workspace.',
  ask_not_found: 'That ask no longer exists.',
  ask_not_open: 'That ask is no longer open.',
  not_the_person_asked: 'Only the person asked can start from this ask.',
  already_started: 'A V2MOM has already been started from this ask.',
  nobody_named: 'Choose at least one person.',
  locked: 'A locked V2MOM can only be deleted by a workspace admin.',
  workspace_required: 'No workspace selected.',
  patch_required: 'Nothing to change.',
};

export function v2momErrorMessage(code: string | null | undefined, fallback = 'Something went wrong.'): string {
  if (!code) return fallback;
  return V2MOM_RPC_ERROR_MESSAGE[code] ?? code;
}

const byPosition = (a: V2momEntry, b: V2momEntry) =>
  a.position - b.position || a.createdAt.localeCompare(b.createdAt);

/** Group flat entries into the page shape. Stable, position-ordered. */
export function buildV2momTree(entries: V2momEntry[]): V2momTree {
  const sorted = [...entries].sort(byPosition);
  const methods = sorted.filter((e) => e.kind === 'method');
  const methodIds = new Set(methods.map((m) => m.id));
  const groups: V2momMethodGroup[] = methods.map((method) => ({
    method,
    obstacles: sorted.filter((e) => e.kind === 'obstacle' && e.methodId === method.id),
    measures: sorted.filter((e) => e.kind === 'measure' && e.methodId === method.id),
  }));
  return {
    values: sorted.filter((e) => e.kind === 'value'),
    methods: groups,
    obstacles: sorted.filter(
      (e) => e.kind === 'obstacle' && (e.methodId == null || !methodIds.has(e.methodId)),
    ),
    measures: sorted.filter(
      (e) => e.kind === 'measure' && (e.methodId == null || !methodIds.has(e.methodId)),
    ),
  };
}

/** Next position for a new entry among its siblings (same kind, same method). */
export function nextV2momPosition(
  entries: V2momEntry[],
  kind: V2momEntryKind,
  methodId: string | null = null,
): number {
  const siblings = entries.filter((e) => e.kind === kind && (e.methodId ?? null) === methodId);
  if (siblings.length === 0) return 0;
  return Math.max(...siblings.map((e) => e.position)) + 1;
}

/**
 * Which of the five sections have anything in them. Drives the completion bars
 * on the list and the "what's missing" line on the editor.
 */
export function v2momSectionFill(v2mom: Pick<V2mom, 'vision'>, entries: V2momEntry[]): Record<V2momSectionKey, boolean> {
  const has = (kind: V2momEntryKind) => entries.some((e) => e.kind === kind && e.title.trim().length > 0);
  return {
    vision: Boolean(v2mom.vision?.trim()),
    values: has('value'),
    methods: has('method'),
    obstacles: has('obstacle'),
    measures: has('measure'),
  };
}

export function v2momFilledCount(v2mom: Pick<V2mom, 'vision'>, entries: V2momEntry[]): number {
  return Object.values(v2momSectionFill(v2mom, entries)).filter(Boolean).length;
}

/** Methods with no measure — the classic V2MOM review finding. */
export function unmeasuredMethods(entries: V2momEntry[]): V2momEntry[] {
  return buildV2momTree(entries)
    .methods.filter((g) => g.measures.length === 0)
    .map((g) => g.method);
}

/**
 * V2MOMs a document may align to: any other document in the workspace that is
 * not itself and not one of its own descendants (no cycles).
 */
export function eligibleParents(self: Pick<V2mom, 'id'>, all: Pick<V2mom, 'id' | 'parentId'>[]): Pick<V2mom, 'id' | 'parentId'>[] {
  const childrenOf = new Map<string, string[]>();
  for (const d of all) {
    if (d.parentId) {
      if (!childrenOf.has(d.parentId)) childrenOf.set(d.parentId, []);
      childrenOf.get(d.parentId)!.push(d.id);
    }
  }
  const excluded = new Set<string>([self.id]);
  const stack = [self.id];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const child of childrenOf.get(cur) ?? []) {
      if (!excluded.has(child)) {
        excluded.add(child);
        stack.push(child);
      }
    }
  }
  return all.filter((d) => !excluded.has(d.id));
}

/** Chain from this document up to the top: [self, parent, grandparent, …]. Stops on a cycle. */
export function alignmentChain(self: V2mom, all: V2mom[]): V2mom[] {
  const byId = new Map(all.map((d) => [d.id, d]));
  const chain: V2mom[] = [self];
  const seen = new Set([self.id]);
  let cur: V2mom | undefined = self;
  while (cur?.parentId && byId.has(cur.parentId) && !seen.has(cur.parentId)) {
    cur = byId.get(cur.parentId)!;
    seen.add(cur.id);
    chain.push(cur);
  }
  return chain;
}

/** Only a draft can be edited; in review it waits; published is readable as written; locked is frozen. */
export function isV2momEditable(v2mom: Pick<V2mom, 'status'>): boolean {
  return v2mom.status === 'draft';
}

// ── The process: who stands where, and what they may do now ──────────────────

/**
 * The reviewer of a V2MOM is the owner of the V2MOM it aligns to. None when it
 * is at the top of its chain, or its parent has no owner. (The database reads
 * the same thing: internal_v2mom_reviewer, migration 945.)
 */
export function v2momReviewerId(doc: Pick<V2mom, 'parentId'>, all: Pick<V2mom, 'id' | 'ownerId'>[]): string | null {
  if (!doc.parentId) return null;
  return all.find((d) => d.id === doc.parentId)?.ownerId ?? null;
}

export interface V2momStanding {
  actorId: string | null;
  reviewerId: string | null;
  isOwner: boolean;
  isReviewer: boolean;
  isAdmin: boolean;
  /** Owner, creator or admin: may edit a draft, send it, ask others against it, delete it. */
  canManage: boolean;
  /** Reviewer or admin: may approve, return, reopen, decline. */
  canReview: boolean;
  /** Sending for review publishes directly: no reviewer, or the reviewer is the author. */
  publishesDirectly: boolean;
}

export function v2momStanding(
  doc: Pick<V2mom, 'ownerId' | 'createdBy' | 'parentId'>,
  all: Pick<V2mom, 'id' | 'ownerId'>[],
  actorId: string | null,
  isAdmin: boolean,
): V2momStanding {
  const reviewerId = v2momReviewerId(doc, all);
  const isOwner = Boolean(actorId) && (doc.ownerId === actorId || doc.createdBy === actorId);
  const isReviewer = Boolean(actorId) && reviewerId === actorId;
  return {
    actorId,
    reviewerId,
    isOwner,
    isReviewer,
    isAdmin,
    canManage: isOwner || isAdmin,
    canReview: isReviewer || isAdmin,
    publishesDirectly: reviewerId == null || reviewerId === actorId,
  };
}

export type V2momActionKey =
  | 'submit'
  | 'withdraw_submission'
  | 'approve'
  | 'return'
  | 'lock'
  | 'request_reopen'
  | 'reopen'
  | 'decline_reopen'
  | 'ask'
  | 'delete';

export interface V2momAction {
  key: V2momActionKey;
  /** One act, one name: the label says what the button does for this person. */
  label: string;
  tone: 'primary' | 'secondary' | 'danger';
  /** The act needs a note (the database refuses without one). */
  needsNote?: boolean;
  /** The act takes an optional note. */
  takesNote?: boolean;
  noteLabel?: string;
}

/**
 * The acts this person may take on this document right now — nothing else is
 * drawn (build contract §2.8: a control the person may not use is not shown).
 * The server check stays the authority.
 */
export function v2momActions(doc: V2mom, standing: V2momStanding): V2momAction[] {
  const out: V2momAction[] = [];
  const { canManage, canReview, isAdmin, publishesDirectly } = standing;

  switch (doc.status) {
    case 'draft':
      if (canManage) {
        out.push({
          key: 'submit',
          label: publishesDirectly ? 'Publish' : 'Send for review',
          tone: 'primary',
          takesNote: !publishesDirectly,
          noteLabel: 'A note for the reviewer (optional)',
        });
      }
      break;
    case 'in_review':
      if (canReview) {
        out.push({ key: 'approve', label: 'Accept', tone: 'primary', takesNote: true, noteLabel: 'A note for the author (optional)' });
        out.push({ key: 'return', label: 'Return with a note', tone: 'secondary', needsNote: true, noteLabel: 'What to change' });
      }
      if (canManage) {
        out.push({ key: 'withdraw_submission', label: 'Take it back', tone: 'secondary' });
      }
      break;
    case 'published':
    case 'locked': {
      const requestPending = Boolean(doc.reopenRequestedAt);
      if (requestPending && canReview) {
        out.push({ key: 'reopen', label: 'Reopen', tone: 'primary', takesNote: true, noteLabel: 'A note for the author (optional)' });
        out.push({ key: 'decline_reopen', label: 'Keep it as it is', tone: 'secondary', takesNote: true, noteLabel: 'Why (optional)' });
      } else if (!requestPending && canManage) {
        const direct = publishesDirectly || isAdmin;
        out.push({
          key: 'request_reopen',
          label: direct ? 'Reopen' : 'Ask to reopen',
          tone: 'secondary',
          needsNote: true,
          noteLabel: 'What has to change, and why now',
        });
      } else if (!requestPending && canReview) {
        out.push({ key: 'reopen', label: 'Reopen', tone: 'secondary', takesNote: true, noteLabel: 'A note for the author (optional)' });
      }
      if (doc.status === 'published' && (canManage || canReview)) {
        out.push({ key: 'lock', label: 'Lock for the period', tone: 'secondary' });
      }
      if (canManage) {
        out.push({ key: 'ask', label: 'Ask people to write theirs', tone: 'secondary' });
      }
      break;
    }
  }

  const mayDelete = doc.status === 'locked' ? isAdmin : canManage;
  if (mayDelete) out.push({ key: 'delete', label: 'Delete', tone: 'danger' });
  return out;
}

/**
 * The plain line that says where the document is and whose move it is, when
 * the next act is not this person's (§2.8: a line in place of a control they
 * may not use). Null when there is nothing to say.
 */
export function v2momWaitingLine(
  doc: V2mom,
  standing: V2momStanding,
  nameOf: (userId: string | null) => string,
): string | null {
  const reviewer = nameOf(standing.reviewerId);
  const owner = nameOf(doc.ownerId);
  switch (doc.status) {
    case 'draft':
      if (!standing.canManage) return `A draft. Only ${owner} can edit it or send it for review.`;
      if (doc.reviewNote && doc.reviewedBy) return `${nameOf(doc.reviewedBy)} returned it: “${doc.reviewNote}”`;
      return null;
    case 'in_review':
      if (standing.canReview) return null;
      return standing.canManage
        ? `Waiting for ${reviewer} to review it.`
        : `${reviewer} is reviewing it.`;
    case 'published':
    case 'locked':
      if (doc.reopenRequestedAt) {
        if (standing.canReview) return null;
        return `${nameOf(doc.reopenRequestedBy)} asked ${reviewer} to reopen it${doc.reopenNote ? `: “${doc.reopenNote}”` : '.'}`;
      }
      if (!standing.canManage && !standing.canReview) {
        return standing.reviewerId
          ? `${V2MOM_STATUS_LABEL[doc.status]}. Only ${owner} or ${reviewer} can change it.`
          : `${V2MOM_STATUS_LABEL[doc.status]}. Only ${owner} can change it.`;
      }
      return null;
  }
}

// ── Due dates ────────────────────────────────────────────────────────────────

export interface DueState {
  /** "Due Oct 20", "Due today", "Overdue 3 days". */
  label: string;
  overdue: boolean;
  daysLeft: number;
}

function utcDay(d: Date): number {
  return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86_400_000);
}

/** A due date (YYYY-MM-DD) read against today, in whole days. */
export function dueState(dueOn: string | null | undefined, today: Date = new Date()): DueState | null {
  if (!dueOn) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dueOn);
  if (!m) return null;
  const due = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86_400_000;
  const daysLeft = due - utcDay(today);
  const dateLabel = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  if (daysLeft < 0) {
    const n = -daysLeft;
    return { label: `Overdue ${n} day${n === 1 ? '' : 's'}`, overdue: true, daysLeft };
  }
  if (daysLeft === 0) return { label: 'Due today', overdue: false, daysLeft };
  return { label: `Due ${dateLabel}`, overdue: false, daysLeft };
}

/** Open asks addressed to a person, oldest due first. */
export function openAsksFor(asks: V2momAsk[], userId: string | null): V2momAsk[] {
  if (!userId) return [];
  return asks
    .filter((a) => a.status === 'open' && a.askedUserId === userId)
    .sort((a, b) => (a.dueOn ?? '9999').localeCompare(b.dueOn ?? '9999') || a.createdAt.localeCompare(b.createdAt));
}

/** Documents waiting on this person: in review where they are the reviewer, or a reopen request addressed to them. */
export function v2momsWaitingOn(all: V2mom[], userId: string | null, isAdmin = false): V2mom[] {
  if (!userId) return [];
  return all.filter((d) => {
    const reviewerId = v2momReviewerId(d, all);
    const reviews = reviewerId === userId || (isAdmin && reviewerId != null && d.ownerId !== userId);
    if (!reviews) return false;
    if (d.status === 'in_review') return true;
    return Boolean(d.reopenRequestedAt) && (d.status === 'published' || d.status === 'locked');
  });
}
